import * as React from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'ramda';

import { selectPreciseFiltersState } from '../../selectors/preciseFilters';
import { PreciseFilters } from '../../components/PreciseFilters/PreciseFilters';
import { selectEstimationByDealType } from '../../selectors/estimation';
import {
  getCheckedFiltersCount,
  useHandleClickModalPrimaryBtn,
  useHandlePreciseFiltersStateChange,
  useHandleResetPreciseFilters,
  useMainPreciseFiltersChangeHandler,
  usePreciseFilters,
} from './utils';
import { useIsReportStatusDefault } from '../../utils/report';
import { selectDealType } from '../../selectors/page';
import { getMoreFiltersText } from './utils/getMoreFiltersText';
import { defaultRefWrapper, TRefWrapper } from '../Scrolling';
import { VIEWED_TIMEOUT } from '../../constants/viewedBlock';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { trackPreciseFiltersView } from '../../utils/teaser';
import { trackPreciseFiltersOpen } from './tracking';
import { selectCategory } from '../../selectors/filters';

interface IPreciseFiltersContainerProps {
  refWrapper?: TRefWrapper;
}

export function PreciseFiltersContainer({
  refWrapper = defaultRefWrapper,
}: IPreciseFiltersContainerProps): React.JSX.Element | null {
  const preciseFiltersData = usePreciseFilters();
  const preciseFiltersState = useSelector(selectPreciseFiltersState);
  const dealType = useSelector(selectDealType);
  const estimation = useSelector(selectEstimationByDealType(dealType));
  const isReportStatusDefault = useIsReportStatusDefault();
  const category = useSelector(selectCategory);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const handleModalOpen = React.useCallback(() => {
    setIsModalOpen(true);
    trackPreciseFiltersOpen();
  }, []);

  const handleModalClose = React.useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handlePreciseFilterStateChange = useHandlePreciseFiltersStateChange();
  const handleResetPreciseFilters = useHandleResetPreciseFilters();
  const handleClickModalPrimaryBtn = useHandleClickModalPrimaryBtn({ handleModalClose });
  const handleMainPreciseFiltersChange = useMainPreciseFiltersChangeHandler();

  /**
   * Скрываем точные фильтры если:
   *  нет данных об оценке
   *  нет json-схемы для построения фильтров
   *  отчет в нередактируемом статусе и не выбраны никакие из точных фильтров
   */
  if (
    !preciseFiltersData ||
    (preciseFiltersData && !preciseFiltersData.mainFilter) ||
    !estimation.data ||
    (!isReportStatusDefault && isEmpty(preciseFiltersState))
  ) {
    return null;
  }

  const checkedFiltersCount = getCheckedFiltersCount(preciseFiltersData.filters, preciseFiltersState);
  const moreFiltersText = getMoreFiltersText(isReportStatusDefault, category);

  return (
    <ActionAfterViewObserver callbackDelay={VIEWED_TIMEOUT} callback={trackPreciseFiltersView}>
      {refWrapper(
        <PreciseFilters
          moreFiltersText={moreFiltersText}
          isDisabledFilters={!isReportStatusDefault}
          // oxlint-disable-next-line typescript/no-non-null-assertion
          mainPreciseFilter={preciseFiltersData.mainFilter!}
          secondaryPreciseFilters={preciseFiltersData.filters}
          preciseFiltersState={preciseFiltersState}
          isModalOpen={isModalOpen}
          mainBtnCount={checkedFiltersCount}
          showModalPrimaryBtnLoader={estimation.isFetching}
          showModalPrimaryBtnError={estimation.isError}
          onModalClose={handleModalClose}
          onChangePreciseFilterState={handlePreciseFilterStateChange}
          onMainPreciseFiltersChange={handleMainPreciseFiltersChange}
          onMoreFiltersButtonClick={handleModalOpen}
          onModalPrimaryBtnClick={handleClickModalPrimaryBtn}
          onModalSecondaryBtnClick={handleResetPreciseFilters}
        />,
      )}
    </ActionAfterViewObserver>
  );
}
